import { useMemo, useState } from "react";
import { Camera, Check, CircleAlert, Clapperboard, ImageOff } from "lucide-react";
import type { Product } from "../domain/types";
import { Button, PageHeader } from "../components/UI";
import { PAUTAS, REGRAS_DO_CANAL, type Pauta as PautaItem } from "../data/pautaTikTok";
import { useWeSell } from "../state/WeSellProvider";

function combina(product: Product, pauta: PautaItem) {
  if (product.status !== "active" || !product.published) return false;
  const porCategoria = pauta.categorias?.length
    ? pauta.categorias.includes(product.category)
    : true;
  if (!porCategoria) return false;
  if (!pauta.termos?.length) return true;
  const alvo = `${product.name} ${product.description}`.toLowerCase();
  return pauta.termos.some((termo) => alvo.includes(termo.toLowerCase()));
}

export function Pauta() {
  const { data, saving, addContent } = useWeSell();
  const [gravando, setGravando] = useState<string | null>(null);
  const [feitas, setFeitas] = useState<string[]>([]);

  const porPauta = useMemo(() => {
    const mapa = new Map<string, Product[]>();
    if (!data) return mapa;
    for (const pauta of PAUTAS) {
      const candidatos = data.products.filter((product) =>
        combina(product, pauta),
      );
      mapa.set(pauta.id, candidatos.slice(0, 12));
    }
    return mapa;
  }, [data]);

  if (!data) return null;

  const semanas = [...new Set(PAUTAS.map((pauta) => pauta.semana))].sort();

  const marcarGravado = async (pauta: PautaItem) => {
    setGravando(pauta.id);
    try {
      await addContent({
        title: pauta.titulo,
        url: "",
        channel: "TikTok",
        type: "reel",
        productIds: (porPauta.get(pauta.id) ?? []).map((product) => product.id),
        publishedAt: null,
      });
      setFeitas((current) => [...current, pauta.id]);
    } finally {
      setGravando(null);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Plano de gravação"
        title="Pauta do TikTok"
        description="Doze vídeos em quatro semanas para transformar o que você já testou em comissão."
      />

      <section className="panel pauta-regras">
        <h2>
          <CircleAlert size={18} aria-hidden="true" /> O que vale para todo vídeo
        </h2>
        <ol>
          {REGRAS_DO_CANAL.map((regra) => (
            <li key={regra}>{regra}</li>
          ))}
        </ol>
      </section>

      {semanas.map((semana) => (
        <section className="pauta-semana" key={semana}>
          <h2 className="pauta-semana__titulo">Semana {semana}</h2>
          <div className="pauta-grid">
            {PAUTAS.filter((pauta) => pauta.semana === semana).map((pauta) => {
              const produtos = porPauta.get(pauta.id) ?? [];
              const poucos = produtos.length < pauta.minimoDeProdutos;
              const jaFeita = feitas.includes(pauta.id);
              return (
                <article className="panel pauta-card" key={pauta.id}>
                  <div className="pauta-card__topo">
                    <span className="content-type">{pauta.formato}</span>
                    <small>{pauta.duracao}</small>
                  </div>
                  <h3>{pauta.titulo}</h3>

                  <p className="pauta-gancho">
                    <strong>Primeira frase:</strong> {pauta.gancho}
                  </p>

                  <ol className="pauta-roteiro">
                    {pauta.roteiro.map((passo) => (
                      <li key={passo}>{passo}</li>
                    ))}
                  </ol>

                  <p className="pauta-gravacao">
                    <Camera size={15} aria-hidden="true" /> {pauta.gravacao}
                  </p>

                  <div className="pauta-produtos">
                    <strong>
                      {produtos.length} produto{produtos.length === 1 ? "" : "s"} da
                      sua vitrine
                    </strong>
                    <div className="pauta-miniaturas">
                      {produtos.map((product) => (
                        <a
                          className="pauta-mini"
                          key={product.id}
                          href={product.affiliateUrl || product.originalUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={product.name}
                        >
                          <span className="pauta-mini__foto">
                            {product.imageUrl ? (
                              <img src={product.imageUrl} alt="" loading="lazy" />
                            ) : (
                              <ImageOff size={16} aria-hidden="true" />
                            )}
                          </span>
                          <span className="pauta-mini__texto">
                            <strong>{product.shortCode}</strong>
                            <small>{product.name}</small>
                          </span>
                        </a>
                      ))}
                    </div>
                    {poucos ? (
                      <small className="pauta-alerta">
                        Este tema pede pelo menos {pauta.minimoDeProdutos} itens.
                        Grave junto com outro vídeo ou cadastre mais produtos
                        dessa categoria.
                      </small>
                    ) : null}
                  </div>

                  <Button
                    onClick={() => void marcarGravado(pauta)}
                    disabled={saving || jaFeita || gravando === pauta.id}
                  >
                    {jaFeita ? (
                      <>
                        <Check size={18} /> Na fila de conteúdos
                      </>
                    ) : (
                      <>
                        <Clapperboard size={18} /> Já gravei este
                      </>
                    )}
                  </Button>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </>
  );
}
